import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  ChevronDown,
  Globe,
  Flag,
  Search,
  AlertCircle,
  BookUser,
  ScanLine,
  Plane,
  Stamp,
  Home,
  Layers,
} from "lucide-react";
import PageHeader from "./PageHeader";
import "./admin-tools.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const CATEGORIES = [
  {
    value: "biographical_page",
    label: "Биографическая страница",
    short: "Биографическая страница",
    tone: "lavender",
    Icon: BookUser,
  },
  {
    value: "mrz",
    label: "MRZ (Машиночитаемая зона)",
    short: "MRZ",
    tone: "sky",
    Icon: ScanLine,
  },
  { value: "visas", label: "Визы", short: "Визы", tone: "mint", Icon: Plane },
  {
    value: "stamps",
    label: "Штампы",
    short: "Штампы",
    tone: "butter",
    Icon: Stamp,
  },
  {
    value: "registration_stamps",
    label: "РВП/ВНЖ/Регистрация",
    short: "РВП/ВНЖ/Регистрация",
    tone: "peach",
    Icon: Home,
  },
];

const DATA_TYPES = [
  { value: "string", label: "Текст (string)" },
  { value: "date", label: "Дата (date)" },
  { value: "number", label: "Число (number)" },
  { value: "boolean", label: "Да/Нет (boolean)" },
  { value: "array", label: "Массив (array)" },
];

const SCOPES = [
  { value: "global", label: "Глобальное (все страны)", icon: Globe },
  { value: "country_specific", label: "Для конкретной страны", icon: Flag },
];

const SCOPE_FILTERS = [
  { value: "", label: "Все области" },
  { value: "global", label: "Глобальные" },
  { value: "country_specific", label: "Для стран" },
];

const plural = (n, [one, few, many]) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

const countries = [
  { code: "AZE", name: "Азербайджан" },
  { code: "ALB", name: "Албания" },
  { code: "DZA", name: "Алжир" },
  { code: "AGO", name: "Ангола" },
  { code: "AND", name: "Андорра" },
  { code: "ATG", name: "Антигуа и Барбуда" },
  { code: "USA", name: "США" },
  { code: "ARG", name: "Аргентина" },
  { code: "ARM", name: "Армения" },
  { code: "AUS", name: "Австралия" },
  { code: "AUT", name: "Австрия" },
  { code: "AFG", name: "Афганистан" },

  { code: "BHS", name: "Багамы" },
  { code: "BHR", name: "Бахрейн" },
  { code: "BGD", name: "Бангладеш" },
  { code: "BRB", name: "Барбадос" },
  { code: "BLR", name: "Беларусь" },
  { code: "BLZ", name: "Белиз" },
  { code: "BEL", name: "Бельгия" },
  { code: "BEN", name: "Бенин" },
  { code: "BGR", name: "Болгария" },
  { code: "BOL", name: "Боливия" },
  { code: "BIH", name: "Босния и Герцеговина" },
  { code: "BWA", name: "Ботсвана" },
  { code: "BRA", name: "Бразилия" },
  { code: "BRN", name: "Бруней" },
  { code: "BFA", name: "Буркина-Фасо" },
  { code: "BDI", name: "Бурundi" },
  { code: "BTN", name: "Бутан" },

  { code: "VUT", name: "Вануату" },
  { code: "VAT", name: "Ватикан" },
  { code: "GBR", name: "Великобритания" },
  { code: "HUN", name: "Венгрия" },
  { code: "VEN", name: "Венесуэла" },
  { code: "VNM", name: "Вьетнам" },

  { code: "GAB", name: "Габон" },
  { code: "HTI", name: "Гаити" },
  { code: "GUY", name: "Гайана" },
  { code: "GMB", name: "Гамбия" },
  { code: "GHA", name: "Гана" },
  { code: "GTM", name: "Гватемала" },
  { code: "GIN", name: "Гвинея" },
  { code: "GNB", name: "Гвинея-Бисау" },
  { code: "DEU", name: "Германия" },
  { code: "HND", name: "Гондурас" },
  { code: "GRD", name: "Гренада" },
  { code: "GEO", name: "Грузия" },
  { code: "GRC", name: "Греция" },

  { code: "DNK", name: "Дания" },
  { code: "DJI", name: "Джибути" },
  { code: "DMA", name: "Доминика" },
  { code: "DOM", name: "Доминиканская Республика" },
  { code: "COD", name: "Демократическая Республика Конго" },

  { code: "EGY", name: "Египет" },

  { code: "ZMB", name: "Замбия" },
  { code: "ZWE", name: "Зимбабве" },

  { code: "ISR", name: "Израиль" },
  { code: "IND", name: "Индия" },
  { code: "IDN", name: "Индонезия" },
  { code: "JOR", name: "Иордания" },
  { code: "IRQ", name: "Ирак" },
  { code: "IRN", name: "Иран" },
  { code: "IRL", name: "Ирландия" },
  { code: "ISL", name: "Исландия" },
  { code: "ESP", name: "Испания" },
  { code: "ITA", name: "Италия" },

  { code: "YEM", name: "Йемен" },

  { code: "CPV", name: "Кабо-Верде" },
  { code: "KAZ", name: "Казахстан" },
  { code: "KHM", name: "Камбоджа" },
  { code: "CMR", name: "Камерун" },
  { code: "CAN", name: "Канада" },
  { code: "QAT", name: "Катар" },
  { code: "KEN", name: "Кения" },
  { code: "CYP", name: "Кипр" },
  { code: "KGZ", name: "Кыргызстан" },
  { code: "KIR", name: "Кирибати" },
  { code: "CHN", name: "Китай" },
  { code: "COL", name: "Колумбия" },
  { code: "COM", name: "Коморы" },
  { code: "COG", name: "Республика Конго" },
  { code: "CRI", name: "Коста-Рика" },
  { code: "CIV", name: "Кот-д'Ивуар" },
  { code: "CUB", name: "Куба" },
  { code: "KWT", name: "Кувейт" },
  { code: "PRK", name: "Северная Корея" },
  { code: "KOR", name: "Южная Корея" },

  { code: "LAO", name: "Лаос" },
  { code: "LVA", name: "Латвия" },
  { code: "LSO", name: "Лесото" },
  { code: "LBR", name: "Либерия" },
  { code: "LBN", name: "Ливан" },
  { code: "LBY", name: "Ливия" },
  { code: "LTU", name: "Литва" },
  { code: "LIE", name: "Лихтенштейн" },
  { code: "LUX", name: "Люксембург" },

  { code: "MUS", name: "Маврикий" },
  { code: "MRT", name: "Мавритания" },
  { code: "MDG", name: "Мадагаскар" },
  { code: "MKD", name: "Северная Македония" },
  { code: "MWI", name: "Малави" },
  { code: "MYS", name: "Малайзия" },
  { code: "MLI", name: "Мали" },
  { code: "MDV", name: "Мальдивы" },
  { code: "MLT", name: "Мальта" },
  { code: "MAR", name: "Марокко" },
  { code: "MHL", name: "Маршалловы Острова" },
  { code: "MEX", name: "Мексика" },
  { code: "FSM", name: "Микронезия" },
  { code: "MOZ", name: "Мозамбик" },
  { code: "MDA", name: "Молдова" },
  { code: "MCO", name: "Монако" },
  { code: "MNG", name: "Монголия" },
  { code: "MMR", name: "Мьянма" },

  { code: "NAM", name: "Намибия" },
  { code: "NRU", name: "Науру" },
  { code: "NPL", name: "Непал" },
  { code: "NER", name: "Нигер" },
  { code: "NGA", name: "Нигерия" },
  { code: "NLD", name: "Нидерланды" },
  { code: "NIC", name: "Никарагуа" },
  { code: "NZL", name: "Новая Зеландия" },
  { code: "NOR", name: "Норвегия" },

  { code: "ARE", name: "ОАЭ" },
  { code: "OMN", name: "Оман" },

  { code: "PAK", name: "Пакистан" },
  { code: "PLW", name: "Палау" },
  { code: "PSE", name: "Палестина" },
  { code: "PAN", name: "Панама" },
  { code: "PNG", name: "Папуа — Новая Гвинея" },
  { code: "PRY", name: "Парагвай" },
  { code: "PER", name: "Перу" },
  { code: "POL", name: "Польша" },
  { code: "PRT", name: "Португалия" },

  { code: "RUS", name: "Россия" },
  { code: "RWA", name: "Руанда" },
  { code: "ROU", name: "Румыния" },

  { code: "SLV", name: "Сальвадор" },
  { code: "WSM", name: "Самоа" },
  { code: "SMR", name: "Сан-Марино" },
  { code: "STP", name: "Сан-Томе и Принсипи" },
  { code: "SAU", name: "Саудовская Аравия" },
  { code: "SWZ", name: "Эсватини" },
  { code: "SYC", name: "Сейшелы" },
  { code: "SEN", name: "Сенегал" },
  { code: "VCT", name: "Сент-Винсент и Гренадины" },
  { code: "KNA", name: "Сент-Китс и Невис" },
  { code: "LCA", name: "Сент-Люсия" },
  { code: "SRB", name: "Сербия" },
  { code: "SGP", name: "Сингапур" },
  { code: "SYR", name: "Сирия" },
  { code: "SVK", name: "Словакия" },
  { code: "SVN", name: "Словения" },
  { code: "SLB", name: "Соломоновы Острова" },
  { code: "SOM", name: "Сомали" },
  { code: "SDN", name: "Судан" },
  { code: "SUR", name: "Суринам" },
  { code: "SLE", name: "Сьерра-Леоне" },

  { code: "TJK", name: "Таджикистан" },
  { code: "THA", name: "Таиланд" },
  { code: "TZA", name: "Танзания" },
  { code: "TGO", name: "Того" },
  { code: "TON", name: "Тонга" },
  { code: "TTO", name: "Тринидад и Тобаго" },
  { code: "TUV", name: "Тувалу" },
  { code: "TUN", name: "Тунис" },
  { code: "TKM", name: "Туркменистан" },
  { code: "TUR", name: "Турция" },

  { code: "UGA", name: "Уганда" },
  { code: "UZB", name: "Узбекистан" },
  { code: "UKR", name: "Украина" },
  { code: "URY", name: "Уругвай" },

  { code: "FJI", name: "Фиджи" },
  { code: "PHL", name: "Филиппины" },
  { code: "FIN", name: "Финляндия" },
  { code: "FRA", name: "Франция" },

  { code: "HRV", name: "Хорватия" },

  { code: "CAF", name: "Центральноафриканская Республика" },
  { code: "TCD", name: "Чад" },
  { code: "MNE", name: "Черногория" },
  { code: "CZE", name: "Чехия" },
  { code: "CHL", name: "Чили" },

  { code: "CHE", name: "Швейцария" },
  { code: "SWE", name: "Швеция" },
  { code: "LKA", name: "Шри-Ланка" },

  { code: "ECU", name: "Эквадор" },
  { code: "GNQ", name: "Экваториальная Гвинея" },
  { code: "ERI", name: "Эритрея" },
  { code: "EST", name: "Эстония" },
  { code: "ETH", name: "Эфиопия" },

  { code: "ZAF", name: "ЮАР" },
  { code: "SSD", name: "Южный Судан" },

  { code: "JAM", name: "Ямайка" },
  { code: "JPN", name: "Япония" },
].sort((a, b) => a.name.localeCompare(b.name, "ru"));

const FieldSchemaPage = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterScope, setFilterScope] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const [formData, setFormData] = useState({
    category: "biographical_page",
    field_key: "",
    scope: "global",
    country_code: "",
    data_type: "string",
    description: "",
    display_name_ru: "",
    display_name_en: "",
    examples: "",
    llm_hint: "",
    required: false,
    order: 0,
  });

  const fetchFields = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/field-schema`,
      );
      setFields(response.data);
      setError("");
    } catch (err) {
      setError("Не удалось загрузить схему полей");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  const handleCreateOrUpdate = async () => {
    try {
      const payload = {
        ...formData,
        examples: formData.examples
          ? formData.examples
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      };

      if (editingField) {
        await axios.put(
          `${API_BASE_URL}/api/admin/field-schema/${editingField.id}`,
          payload,
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/field-schema`, payload);
      }

      fetchFields();
      resetForm();
    } catch (err) {
      alert(err.response?.data?.error || "Ошибка при сохранении");
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setIsCreating(true);
    setFormData({
      category: field.category,
      field_key: field.field_key,
      scope: field.scope,
      country_code: field.country_code || "",
      data_type: field.data_type,
      description: field.description || "",
      display_name_ru: field.display_name_ru || "",
      display_name_en: field.display_name_en || "",
      examples: Array.isArray(field.examples) ? field.examples.join(", ") : "",
      llm_hint: field.llm_hint || "",
      required: field.required || false,
      order: field.order || 0,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить это поле из схемы?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/field-schema/${id}`);
      fetchFields();
    } catch (err) {
      alert("Ошибка при удалении");
    }
  };

  const resetForm = () => {
    setIsCreating(false);
    setEditingField(null);
    setFormData({
      category: "biographical_page",
      field_key: "",
      scope: "global",
      country_code: "",
      data_type: "string",
      description: "",
      display_name_ru: "",
      display_name_en: "",
      examples: "",
      llm_hint: "",
      required: false,
      order: 0,
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const matchesSearchAndScope = (f) => {
    const matchesSearch =
      !searchQuery ||
      f.field_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.display_name_ru?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.display_name_en?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesScope = !filterScope || f.scope === filterScope;
    return matchesSearch && matchesScope;
  };

  const filteredFields = fields.filter((f) => {
    const matchesCategory = !filterCategory || f.category === filterCategory;
    return matchesSearchAndScope(f) && matchesCategory;
  });

  // счётчики на вкладках учитывают поиск и область, но не саму категорию
  const categoryCounts = fields
    .filter(matchesSearchAndScope)
    .reduce((acc, f) => {
      acc[f.category] = (acc[f.category] || 0) + 1;
      acc.all = (acc.all || 0) + 1;
      return acc;
    }, {});

  const groupedFields = filteredFields.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field);
    return acc;
  }, {});

  Object.keys(groupedFields).forEach((cat) => {
    groupedFields[cat].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  const header = (
    <PageHeader
      title="Схема полей паспорта"
      subtitle="Поля, которые система распознаёт в каждой категории данных"
    >
      <button
        type="button"
        className="px-btn px-btn-ink"
        onClick={() => setIsCreating(true)}
      >
        <Plus size={20} strokeWidth={2.5} aria-hidden="true" />
        Добавить поле
      </button>
    </PageHeader>
  );

  if (loading) {
    return (
      <div className="tool-page">
        {header}
        <div className="px-empty" role="status">
          Загрузка...
        </div>
      </div>
    );
  }

  const setField = (key) => (e) =>
    setFormData({ ...formData, [key]: e.target.value });

  return (
    <div className="tool-page">
      {header}

      {error && (
        <div className="tool-alert" role="alert">
          <AlertCircle size={20} aria-hidden="true" />
          {error}
        </div>
      )}

      {isCreating && (
        <section
          className="res-card tool-form"
          aria-labelledby="field-form-title"
        >
          <div className="res-card-head tool-card-head">
            <div>
              <h2 className="res-card-title" id="field-form-title">
                {editingField ? "Редактирование поля" : "Новое поле"}
              </h2>
            </div>
            <button
              type="button"
              className="px-icon-btn is-small"
              onClick={resetForm}
              aria-label="Отмена"
              title="Отмена"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <div className="rec-fields">
            <label className="rec-field">
              <span className="rec-label">
                Категория
                <span className="tool-req" aria-hidden="true">
                  *
                </span>
              </span>
              <select
                className="rec-select"
                value={formData.category}
                onChange={setField("category")}
                aria-required="true"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="rec-field">
              <span className="rec-label">
                Ключ поля (field_key)
                <span className="tool-req" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                className="tool-input is-mono"
                type="text"
                placeholder="full_name"
                value={formData.field_key}
                onChange={setField("field_key")}
                aria-required="true"
              />
            </label>

            <label className="rec-field">
              <span className="rec-label">
                Область действия
                <span className="tool-req" aria-hidden="true">
                  *
                </span>
              </span>
              <select
                className="rec-select"
                value={formData.scope}
                onChange={setField("scope")}
                aria-required="true"
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>

            {formData.scope === "country_specific" && (
              <label className="rec-field">
                <span className="rec-label">
                  Код страны
                  <span className="tool-req" aria-hidden="true">
                    *
                  </span>
                </span>
                <select
                  className={`rec-select${formData.country_code ? "" : " is-empty"}`}
                  value={formData.country_code}
                  onChange={setField("country_code")}
                  aria-required="true"
                >
                  <option value="">Выберите...</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="rec-field">
              <span className="rec-label">Тип данных</span>
              <select
                className="rec-select"
                value={formData.data_type}
                onChange={setField("data_type")}
              >
                {DATA_TYPES.map((dt) => (
                  <option key={dt.value} value={dt.value}>
                    {dt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="rec-field">
              <span className="rec-label">Название (RU)</span>
              <input
                className="tool-input"
                type="text"
                placeholder="Полное имя"
                value={formData.display_name_ru}
                onChange={setField("display_name_ru")}
              />
            </label>

            <label className="rec-field">
              <span className="rec-label">Название (EN)</span>
              <input
                className="tool-input"
                type="text"
                placeholder="Full Name"
                value={formData.display_name_en}
                onChange={setField("display_name_en")}
              />
            </label>

            <label className="rec-field">
              <span className="rec-label">Порядок сортировки</span>
              <input
                className="tool-input"
                type="number"
                value={formData.order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    order: parseInt(e.target.value) || 0,
                  })
                }
              />
            </label>

            <label className="rec-field tool-field-wide">
              <span className="rec-label">Описание</span>
              <textarea
                className="tool-input"
                rows={2}
                placeholder="Описание поля для внутреннего использования"
                value={formData.description}
                onChange={setField("description")}
              />
            </label>

            <label className="rec-field tool-field-wide">
              <span className="rec-label">Примеры (через запятую)</span>
              <input
                className="tool-input"
                type="text"
                placeholder="IVANOV IVAN IVANOVICH, ПЕТРОВ ПЕТР ПЕТРОВИЧ"
                value={formData.examples}
                onChange={setField("examples")}
              />
            </label>

            <label className="rec-field tool-field-wide">
              <span className="rec-label">Подсказка для системы</span>
              <textarea
                className="tool-input"
                rows={3}
                placeholder="Инструкция для системы, как извлекать это поле"
                value={formData.llm_hint}
                onChange={setField("llm_hint")}
              />
            </label>
          </div>

          <label className="rec-checkbox">
            <input
              type="checkbox"
              checked={formData.required}
              onChange={(e) =>
                setFormData({ ...formData, required: e.target.checked })
              }
            />
            Обязательное поле
          </label>

          <div className="tool-form-actions">
            <button
              type="button"
              className="px-btn px-btn-outline"
              onClick={resetForm}
            >
              Отмена
            </button>
            <button
              type="button"
              className="px-btn px-btn-ink"
              onClick={handleCreateOrUpdate}
              disabled={!formData.field_key || !formData.category}
            >
              <Save size={18} aria-hidden="true" />
              {editingField ? "Сохранить изменения" : "Создать поле"}
            </button>
          </div>
        </section>
      )}

      <div className="tool-filters">
        <div className="tool-filter-row">
          <div className="hist-tabs" role="radiogroup" aria-label="Категория">
            {[{ value: "", short: "Все", tone: null }, ...CATEGORIES].map(
              (cat) => {
                const active = filterCategory === cat.value;
                return (
                  <button
                    key={cat.value || "all"}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`hist-tab${cat.tone ? ` tone-${cat.tone}` : ""}${active ? " is-active" : ""}`}
                    onClick={() => setFilterCategory(cat.value)}
                  >
                    {cat.short}
                    <span className="hist-tab-count">
                      {categoryCounts[cat.value || "all"] || 0}
                    </span>
                  </button>
                );
              },
            )}
          </div>
        </div>
        <div className="tool-filter-row">
          <div
            className="tool-seg"
            role="radiogroup"
            aria-label="Область действия"
          >
            {SCOPE_FILTERS.map((s) => (
              <button
                key={s.value || "all"}
                type="button"
                role="radio"
                aria-checked={filterScope === s.value}
                onClick={() => setFilterScope(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <label className="hist-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              aria-label="Поиск по ключу или названию"
              placeholder="Поиск по ключу или названию"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </label>
        </div>
      </div>

      {Object.keys(groupedFields).length === 0 ? (
        <div className="px-empty tool-empty">
          <span className="tool-empty-icon tone-lavender" aria-hidden="true">
            <Layers size={26} />
          </span>
          Нет полей, соответствующих фильтрам
        </div>
      ) : (
        Object.entries(groupedFields).map(([category, categoryFields]) => {
          const categoryConfig = CATEGORIES.find((c) => c.value === category);
          const categoryLabel = categoryConfig?.label || category;
          const CategoryIcon = categoryConfig?.Icon || Layers;
          const isExpanded = expandedCategories[category] !== false;
          const bodyId = `field-group-${category}`;

          return (
            <section key={category} className="res-card tool-group">
              <h2 className="tool-group-heading">
                <button
                  type="button"
                  className="tool-group-toggle"
                  onClick={() => toggleCategory(category)}
                  aria-expanded={isExpanded}
                  aria-controls={bodyId}
                >
                  <span
                    className={`tool-group-icon${categoryConfig ? ` tone-${categoryConfig.tone}` : ""}`}
                    aria-hidden="true"
                  >
                    <CategoryIcon size={22} />
                  </span>
                  <span className="tool-group-text">
                    <span className="res-card-title">{categoryLabel}</span>
                    <span className="tool-group-count">
                      {categoryFields.length}{" "}
                      {plural(categoryFields.length, ["поле", "поля", "полей"])}
                    </span>
                  </span>
                  <span className="tool-chevron" aria-hidden="true">
                    <ChevronDown size={20} strokeWidth={2.5} />
                  </span>
                </button>
              </h2>

              {isExpanded && (
                <div className="tool-group-body" id={bodyId}>
                  <div className="px-table-wrap">
                    <table className="tool-table">
                      <caption className="sr-only">{categoryLabel}</caption>
                      <thead>
                        <tr>
                          <th scope="col">Ключ</th>
                          <th scope="col">Название</th>
                          <th scope="col">Тип</th>
                          <th scope="col">Область</th>
                          <th scope="col">Страна</th>
                          <th scope="col" className="is-center">
                            Обяз.
                          </th>
                          <th scope="col" className="is-center">
                            Порядок
                          </th>
                          <th scope="col">
                            <span className="sr-only">Действия</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryFields.map((field) => (
                          <tr key={field.id}>
                            <td className="is-wide">
                              <code className="tool-key">
                                {field.field_key}
                              </code>
                            </td>
                            <td
                              className={`is-wide${field.display_name_ru || field.display_name_en ? "" : " is-empty-mobile"}`}
                            >
                              <span className="tool-name">
                                {field.display_name_ru || (
                                  <span className="tool-muted">—</span>
                                )}
                              </span>
                              {field.display_name_en && (
                                <span className="tool-name-en">
                                  {field.display_name_en}
                                </span>
                              )}
                            </td>
                            <td>
                              <span className="tool-cell-label">Тип</span>
                              <span className="res-badge tone-sky tool-type">
                                {field.data_type}
                              </span>
                            </td>
                            <td>
                              <span className="tool-cell-label">Область</span>
                              <span
                                className={`tool-scope ${field.scope === "global" ? "is-global" : "is-country"}`}
                              >
                                {field.scope === "global" ? (
                                  <Globe size={16} aria-hidden="true" />
                                ) : (
                                  <Flag size={16} aria-hidden="true" />
                                )}
                                {field.scope === "global"
                                  ? "Глобальное"
                                  : "Страна"}
                              </span>
                            </td>
                            <td
                              className={
                                field.country_code ? "" : "is-empty-mobile"
                              }
                            >
                              <span className="tool-cell-label">Страна</span>
                              {field.country_code ? (
                                <span className="res-badge is-warn">
                                  {field.country_code}
                                </span>
                              ) : (
                                <span className="tool-muted">—</span>
                              )}
                            </td>
                            <td className="is-center">
                              <span className="tool-cell-label">
                                Обязательное
                              </span>
                              {field.required ? (
                                <span className="res-badge is-warn">Да</span>
                              ) : (
                                <span className="tool-muted">Нет</span>
                              )}
                            </td>
                            <td className="is-center">
                              <span className="tool-cell-label">Порядок</span>
                              {field.order}
                            </td>
                            <td className="tool-row-actions">
                              <div
                                className="tool-actions"
                                style={{ justifyContent: "flex-end" }}
                              >
                                <button
                                  type="button"
                                  className="px-icon-btn is-small"
                                  onClick={() => {
                                    handleEdit(field);
                                    window.scrollTo({
                                      top: 0,
                                      behavior: "smooth",
                                    });
                                  }}
                                  aria-label={`Редактировать ${field.field_key}`}
                                  title="Редактировать"
                                >
                                  <Pencil size={18} aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="px-icon-btn is-small is-danger"
                                  onClick={() => handleDelete(field.id)}
                                  aria-label={`Удалить ${field.field_key}`}
                                  title="Удалить"
                                >
                                  <Trash2 size={18} aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default FieldSchemaPage;
